import { AxiosResponse } from 'axios';
import { takeLatest, call, put, all, select, delay, debounce, fork, throttle, takeLeading, take, race } from 'redux-saga/effects';
import { DuplicateOrderResponse, NuviaOrderItem, NuviaOrdersResponse, OrderItem, OrderItemAttachment, OrderItemReports, OrdersResponse, OrganizationItem, OrganizationsResponse, SignInResponse, UpdateOrderResponse, UserGroupTest, UsersResponse } from '../../api/types';
import { SimplyTestApi } from '../../api/simplytest';
import { NEEDS_CONTACT_LAB, NEEDS_CONTACT_NOTHING, NEEDS_CONTACT_PROVIDER, ROUTES } from '../../shared/constants';
import { selectConfig } from '../../shared/redux/slice';
import { IConfiguration } from '../../shared/types';
import authSlice, { selectIdToken, selectUserOrgId, unauthorized } from '../../auth/redux/slice';
import { toast } from 'react-hot-toast';
import ordersSearchSlice, { createOrder, duplicateOrder, filterKitOrders, filterOrders, loadFilterAvailableTests, loadKitOrders, loadOrder, loadOrderCreate, loadOrderEdit, loadOrderOrganization, loadOrderOrganizationProviders, loadOrderOrganizationTests, loadOrderOrganizations, loadOrderProviders, loadOrderTests, loadOrders, markLabMessageRead, resetOrderForm, saveOrderAttachment, selectOrdersSearchGridParams, selectOrdersSearchGridSearch, selectOrdersSearchOrders, selectOrdersSearchSelectedOrder, selectOrdersSearchSelectedOrderId, selectOrdersSearchUploadAttachmentFile, sendLabMessage, updateOrder, viewOrder } from "./slice";
import { formatUtc, parseDataUrl } from '../../shared/utils';
import { LoadOrderAvailableTestsPayload, OrderGridParams } from '../types';
import { generatePath } from 'react-router';
import { OrderStatus } from '../add-edit/types';

export abstract class OrderSagas {
    public static* run(){
        try {
            yield all([
                fork(OrderSagas.debounceFilterOrders),
                fork(OrderSagas.debounceFilterKitOrders),
                takeLatest(loadOrders, OrderSagas.loadOrders),
                takeLatest(loadKitOrders, OrderSagas.loadKitOrders),
                takeLatest(loadOrder, OrderSagas.loadOrder),
                takeLatest(viewOrder, OrderSagas.viewOrder),
                takeLatest(saveOrderAttachment, OrderSagas.saveOrderAttachment),
                takeLatest(createOrder, OrderSagas.createOrder),
                takeLatest(updateOrder, OrderSagas.updateOrder),
                takeLatest(resetOrderForm, OrderSagas.resetOrderForm),
                takeLatest(duplicateOrder, OrderSagas.duplicateOrder),
                takeLatest(loadOrderOrganization, OrderSagas.loadOrderOrganization),
                takeLatest(loadOrderOrganizations, OrderSagas.loadOrderOrganizations),
                takeLatest(loadOrderProviders, OrderSagas.loadOrderProviders),
                takeLatest(markLabMessageRead, OrderSagas.markLabMessageRead),
                takeLatest(sendLabMessage, OrderSagas.sendLabMessage),
                takeLatest(loadOrderTests, OrderSagas.loadOrderTests),
                takeLatest(loadOrderOrganizationTests, OrderSagas.loadOrderOrganizationTests),
                takeLatest(loadOrderEdit, OrderSagas.loadOrderEdit),
                takeLatest(loadOrderCreate, OrderSagas.loadOrderCreate),
                takeLatest(loadOrderOrganizationProviders, OrderSagas.loadOrderOrganizationProviders),
                takeLatest(loadFilterAvailableTests, OrderSagas.loadFilterAvailableTests),
            ]);
        }
        catch(e: any){
            console.log(`[OrderSagas:run] the following exception has occurred`, e);
        }
    }

    private static* loadFilterAvailableTests(action: ReturnType<typeof loadFilterAvailableTests>){
        try {
            //yield put(ordersSearchSlice.actions.setOrderState('BUSY'));
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);
            
            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const userTestsResponse: AxiosResponse<UserGroupTest[]> = yield call(
                {
                    context: client,
                    fn: client.getUserAvailableTests,
                },
            );

            if(userTestsResponse.status === 401)
            {
                yield put(unauthorized(undefined));
                return;     
            }
            
            if(userTestsResponse.status !== 200)
                throw new Error("Error communicating with Api");

            yield put(ordersSearchSlice.actions.setFilterAvailableTests(userTestsResponse.data));
        }
        catch(e: any){
            toast.error('Could not load filter tests.');
            console.log(`[OrderSagas:loadOrderOrganizationTests] the following exception has occurred`, e);
        }
        finally{
            //yield put(ordersSearchSlice.actions.setOrderState('READY'))
        }
    }

    private static* loadOrderOrganizationProviders(action: ReturnType<typeof loadOrderOrganizationProviders>) {
        try {
            const { payload: orgId } = action;
            yield put(ordersSearchSlice.actions.setOrderState('BUSY'));
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);
            
            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const orgUsersResponse: AxiosResponse<UsersResponse> = yield call(
                {
                    context: client,
                    fn: client.getOrganizationUsers,
                },
                orgId
            );

            if(orgUsersResponse.status === 401)
            {
                yield put(unauthorized(undefined));
                return;     
            }
            
            if(orgUsersResponse.status !== 200)
                throw new Error("Error communicating with Api");

            yield put(ordersSearchSlice.actions.setOrderProviders(orgUsersResponse.data));
        }
        catch(e: any){
            toast.error('Could not load org providers.');
            console.log(`[OrderSagas:loadOrderOrganizationProviders] the following exception has occurred`, e);
        }
        finally{
            yield put(ordersSearchSlice.actions.setOrderState('READY'))
        }
    }

    private static* loadOrderOrganizationTests(action: ReturnType<typeof loadOrderOrganizationTests>){
        try {
            const { payload: orgId } = action;
            yield put(ordersSearchSlice.actions.setOrderState('BUSY'));
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);
            
            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const orgTestsResponse: AxiosResponse<UserGroupTest[]> = yield call(
                {
                    context: client,
                    fn: client.getOrganizationAvailableTests,
                },
                orgId!
            );

            if(orgTestsResponse.status === 401)
            {
                yield put(unauthorized(undefined));
                return;     
            }
            
            if(orgTestsResponse.status !== 200)
                throw new Error("Error communicating with Api");

            yield put(ordersSearchSlice.actions.setOrderTests(orgTestsResponse.data));
        }
        catch(e: any){
            toast.error('Could not load org tests.');
            console.log(`[OrderSagas:loadOrderOrganizationTests] the following exception has occurred`, e);
        }
        finally{
            yield put(ordersSearchSlice.actions.setOrderState('READY'))
        }
    }

    private static* loadOrderCreate(action: ReturnType<typeof loadOrderCreate>){
        try {
            yield put(ordersSearchSlice.actions.setOrderState('BUSY'));
            
            yield call(
                {
                    context: OrderSagas,
                    fn: OrderSagas.loadOrderTests,
                },
                loadOrderTests(),
            );

            yield call(
                {
                    context: OrderSagas,
                    fn: OrderSagas.loadOrderProviders,
                },
                loadOrderProviders(),
            );

            yield call(
                {
                    context: OrderSagas,
                    fn: OrderSagas.loadOrderOrganizations,
                },
                loadOrderOrganizations(),
            );

            // Always load the logged-in user's own org, regardless of role - "Organization Admin"
            // is flagged isAdmin=true but the Organization section still shows that user their own
            // org details (not the multi-org dropdown), so it needs this loaded the same as anyone else.
            // Harmless no-op for true multi-org admins with no single org claim (fails silently).
            yield call(
                {
                    context: OrderSagas,
                    fn: OrderSagas.loadOrderOrganization,
                },
                loadOrderOrganization(),
            );
        }
        catch(e: any){
            toast.error('Could not load lookups.');
            console.log(`[OrderSagas:loadOrderCreate] the following exception has occurred`, e);
        }
        finally{
            yield put(ordersSearchSlice.actions.setOrderState('READY'))
        }
    }

    private static* loadOrderEdit(action: ReturnType<typeof loadOrderEdit>){
        try {
            const { payload: id } = action;
            yield put(ordersSearchSlice.actions.setOrderState('BUSY'));
            yield call(
                {
                    context: OrderSagas,
                    fn: OrderSagas.loadOrder,
                },
                loadOrder({ id })
            );

            yield call(
                {
                    context: OrderSagas,
                    fn: OrderSagas.loadOrderTests,
                },
                loadOrderTests(),
            );

            yield call(
                {
                    context: OrderSagas,
                    fn: OrderSagas.loadOrderProviders,
                },
                loadOrderProviders(),
            );

            yield call(
                {
                    context: OrderSagas,
                    fn: OrderSagas.loadOrderOrganizations,
                },
                loadOrderOrganizations(),
            );
        }
        catch(e: any){
            toast.error('Could not load order.');
            console.log(`[OrderSagas:loadEditOrder] the following exception has occurred`, e);
        }
        finally{
            yield put(ordersSearchSlice.actions.setOrderState('READY'))
        }
    }

    private static* loadOrderTests(action: ReturnType<typeof loadOrderTests>){
        try {
            const orderId: number = yield select(selectOrdersSearchSelectedOrderId);
            yield put(ordersSearchSlice.actions.setOrderState('BUSY'));
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);
            
            console.log('tests', orderId);
            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const orderTestsResponse: AxiosResponse<UserGroupTest[]> = yield call(
                {
                    context: client,
                    fn: client.getOrderAvailableTests,
                },
                orderId ?? 0
            );

            if(orderTestsResponse.status === 401)
            {
                yield put(unauthorized(undefined));
                return;     
            }
            
            if(orderTestsResponse.status !== 200)
                throw new Error("Error communicating with Api");

            yield put(ordersSearchSlice.actions.setOrderTests(orderTestsResponse.data));
        }
        catch(e: any){
            toast.error('Could not load order tests.');
            console.log(`[OrderSagas:loadOrderTests] the following exception has occurred`, e);
        }
        finally{
            yield put(ordersSearchSlice.actions.setOrderState('READY'))
        }
    }

    private static* markLabMessageRead(action: ReturnType<typeof markLabMessageRead>){
        try {
            yield put(ordersSearchSlice.actions.setSelectedOrderMessagesState('BUSY'));
            const {orderId, ...order} = yield select(selectOrdersSearchSelectedOrder);
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);
            
            const updatededOrder: OrderItem = {
                ...order,
                needsContact: NEEDS_CONTACT_NOTHING,
            }

            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const markMsgReadResp: AxiosResponse<void> = yield call(
                {
                    context: client,
                    fn: client.updateOrder,
                },
                orderId,
                updatededOrder,
            );

            if(markMsgReadResp.status === 401)
            {
                yield put(unauthorized(undefined));
                return;     
            }
            
            if(markMsgReadResp.status !== 204)
                throw new Error("Error communicating with Api");
            
            yield put(ordersSearchSlice.actions.setSelectedOrder({ ...updatededOrder, orderId}));
            const orders: OrderItem[] = yield select(selectOrdersSearchOrders);
            const orderIndex = orders.findIndex(order => order.orderId === orderId);

            yield put(ordersSearchSlice.actions.setOrders([
                ...orders.slice(0, orderIndex),
                {...updatededOrder, orderId},
                ...orders.slice(orderIndex + 1),
            ]));
        }
        catch(e: any){
            toast.error('Could not update needs contact.');
            console.log(`[OrderSagas:markLabMessageRead] the following exception has occurred`, e);
        }
        finally{
            yield put(ordersSearchSlice.actions.setSelectedOrderMessagesState('READY'))
        }
    }

    private static* sendLabMessage(action: ReturnType<typeof sendLabMessage>){
        const { payload: { internalComment }} = action;
        try {
            yield put(ordersSearchSlice.actions.setSelectedOrderMessagesState('BUSY'));
            const {orderId, ...order} = yield select(selectOrdersSearchSelectedOrder);
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);
            
            const updatededOrder: OrderItem = {
                ...order,
                internalComment,
                needsContact: NEEDS_CONTACT_LAB,
            }

            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const sendMessageResp: AxiosResponse<void> = yield call(
                {
                    context: client,
                    fn: client.updateOrder,
                },
                orderId,
                updatededOrder,
            );

            if(sendMessageResp.status === 401)
            {
                yield put(unauthorized(undefined));
                return;     
            }
            
            if(sendMessageResp.status !== 204)
                throw new Error("Error communicating with Api");
            yield put(ordersSearchSlice.actions.setSelectedOrder({ ...updatededOrder, orderId}));
            yield call({ context: toast, fn: toast.success}, `Message sent to lab.`);
        }
        catch(e: any){
            toast.error('Could not send lab message.');
            console.log(`[OrderSagas:sendLabMessage] the following exception has occurred`, e);
        }
        finally{
            yield put(ordersSearchSlice.actions.setSelectedOrderMessagesState('READY'))
        }
    }

    private static* loadOrderOrganizations(action: ReturnType<typeof loadOrderOrganizations>){
        try {
            yield put(ordersSearchSlice.actions.setOrderState('BUSY'));
            const orgId: string | undefined = yield select(selectUserOrgId);
            if(orgId === undefined)
                throw new Error('Could not find users organization.');

            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);
            
            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const orgsResponse: AxiosResponse<OrganizationsResponse> = yield call(
                {
                    context: client,
                    fn: client.allOrganizations,
                },
            );

            if(orgsResponse.status === 401)
            {
                yield put(unauthorized(undefined));
                return;     
            }
            
            if(orgsResponse.status !== 200)
                throw new Error("Error communicating with Api");

            yield put(ordersSearchSlice.actions.setOrderOrganizations(orgsResponse.data));
        }
        catch(e: any){
            toast.error('Could not order organizations.');
            console.log(`[OrderSagas:loadOrderOrganizations] the following exception has occurred`, e);
        }
        finally{
            yield put(ordersSearchSlice.actions.setOrderState('READY'))
        }
    }

    private static* loadOrderOrganization(action: ReturnType<typeof loadOrderOrganization>){
        try {
            yield put(ordersSearchSlice.actions.setOrderState('BUSY'));
            const orgId: string | undefined = yield select(selectUserOrgId);
            if(orgId === undefined)
                throw new Error('Could not find users organization.');

            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);
            
            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const userOrgResponse: AxiosResponse<OrganizationItem> = yield call(
                {
                    context: client,
                    fn: client.getOrganization,
                },
                orgId,
            );

            if(userOrgResponse.status === 401)
            {
                yield put(unauthorized(undefined));
                return;     
            }
            
            if(userOrgResponse.status !== 200)
                throw new Error("Error communicating with Api");

            yield put(ordersSearchSlice.actions.setOrderOrganization(userOrgResponse.data));
        }
        catch(e: any){
            toast.error('Could not order organization info.');
            console.log(`[OrderSagas:loadOrderOrganization] the following exception has occurred`, e);
        }
        finally{
            yield put(ordersSearchSlice.actions.setOrderState('READY'))
        }
    }

    private static* loadOrderProviders(action: ReturnType<typeof loadOrderProviders>){
        try {
            yield put(ordersSearchSlice.actions.setOrderState('BUSY'));
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);
            
            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const userProvidersResponse: AxiosResponse<UsersResponse> = yield call(
                {
                    context: client,
                    fn: client.allUsers,
                },
            );

            if(userProvidersResponse.status === 401)
            {
                yield put(unauthorized(undefined));
                return;     
            }
            
            if(userProvidersResponse.status !== 200)
                throw new Error("Error communicating with Api");

            yield put(ordersSearchSlice.actions.setOrderProviders(userProvidersResponse.data));
        }
        catch(e: any){
            toast.error('Could not load order providers.');
            console.log(`[OrderSagas:loadOrderProviders] the following exception has occurred`, e);
        }
        finally{
            yield put(ordersSearchSlice.actions.setOrderState('READY'))
        }
    }

    private static* duplicateOrder(action: ReturnType<typeof duplicateOrder>){
        const { payload: { orderId, navigate } } = action;
        try {
            // Sep 2026 - this saga never touched orderState before, so OrderDetailsDrawer's "Copy
            // Order" button had no loading/disabled feedback at all during the network round trip -
            // a user could click it repeatedly and fire duplicateOrder more than once, each one a
            // real new order. Reuses the same orderState/selectOrdersSearchOrderIsBusy the Create/
            // Edit Order form already drives its own submit button from - safe to share since this
            // saga and OrderForm's are never in flight from the same screen at once.
            yield put(ordersSearchSlice.actions.setOrderState('BUSY'));
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);
            
            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const duplicateOrderResponse: AxiosResponse<DuplicateOrderResponse> = yield call(
                {
                    context: client,
                    fn: client.duplicateOrder,
                },
                orderId,
            );

            if(duplicateOrderResponse.status === 401)
            {
                yield put(unauthorized(undefined));
                return;     
            }
            
            if(duplicateOrderResponse.status !== 200)
                throw new Error("Error communicating with Api");

            yield call({ context: toast, fn: toast.success}, `Order #${duplicateOrderResponse.data.orderId} created from Order #${orderId}!`);
            const duplicateOrder: OrderItem = {
                ...duplicateOrderResponse.data,
            }

            yield put(ordersSearchSlice.actions.setSelectedOrder(duplicateOrder));
            navigate(`/orders/${duplicateOrderResponse.data.orderId}`);
        }
        catch(e: any){
            toast.error('Could not duplicate order.');
            console.log(`[OrderSagas:duplicateOrder] the following exception has occurred`, e);
        }
        finally {
            yield put(ordersSearchSlice.actions.setOrderState('READY'));
        }
    }

    private static* resetOrderForm(action: ReturnType<typeof resetOrderForm>){
        try {
            yield put(ordersSearchSlice.actions.setOrderState('RESET'));
        }
        catch(e: any){
            toast.error('Unable to reset form.');
            console.log(`[OrderSagas:resetOrderForm] the following exception has occurred`, e);
        }
    }

    private static* createOrder(action: ReturnType<typeof createOrder>){
        const { payload: { order, navigate }} = action;
        try {
            yield put(ordersSearchSlice.actions.setOrderState('BUSY'));
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);
            
            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const createOrderResponse: AxiosResponse<UpdateOrderResponse> = yield call(
                {
                    context: client,
                    fn: client.createOrder,
                },
                {
                    ...order,
                    orderStatus: OrderStatus.OPEN,
                } as OrderItem
            );

            if(createOrderResponse.status === 401)
            {
                yield put(unauthorized(undefined));
                return;     
            }
            
            if(createOrderResponse.status !== 200)
                throw new Error("Error communicating with Api");

            yield put(ordersSearchSlice.actions.setOrderState('SUCCESS'));
            navigate(generatePath(ROUTES.Orders));
        }
        catch(e: any){
            toast.error('Unable to create order.');
            console.log(`[OrderSagas:createOrder] the following exception has occurred`, e);
            yield put(ordersSearchSlice.actions.setOrderState('READY'));
        }
    }

    private static* updateOrder(action: ReturnType<typeof updateOrder>){
        const { payload: { orderId, order, navigate }} = action;
        try {
            yield put(ordersSearchSlice.actions.setOrderState('BUSY'));
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);
            
            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const updateOrderResponse: AxiosResponse<void> = yield call(
                {
                    context: client,
                    fn: client.updateOrder,
                },
                orderId,
                {
                    ...order as OrderItem,
                    needsContact: order.updateComment ? NEEDS_CONTACT_PROVIDER : NEEDS_CONTACT_NOTHING,
                }
            );

            if(updateOrderResponse.status === 401)
            {
                yield put(unauthorized(undefined));
                return;     
            }
            
            if(updateOrderResponse.status !== 204)
                throw new Error("Error communicating with Api");
                
            const currentOrder: OrderItem = yield select(selectOrdersSearchSelectedOrder);
            yield put(ordersSearchSlice.actions.setSelectedOrder({...action.payload, ...order} as any));
            yield call({ context: toast, fn: toast.success}, `Order #${orderId} Updated!`);
            navigate(generatePath(ROUTES.Orders));
        }
        catch(e: any){
            toast.error('Unable to update order.');
            console.log(`[OrderSagas:updateOrder] the following exception has occurred`, e);
        }
        finally {
            yield put(ordersSearchSlice.actions.setOrderState('READY'));
        }
    }

    private static* saveOrderAttachment(action: ReturnType<typeof saveOrderAttachment>){
        try {
            yield put(ordersSearchSlice.actions.setUploadAttachmentState('BUSY'));
            const currentOrder: OrderItem | undefined = yield select(selectOrdersSearchSelectedOrder);
            if(currentOrder === undefined)
                throw new Error('Could not find the current order in redux.');
            
            const attachmentDataUrl: string | undefined = yield select(selectOrdersSearchUploadAttachmentFile);
            if(attachmentDataUrl === undefined)
                throw new Error('Attachment data url was undefined.');

            const base64Attachment: string = yield call(parseDataUrl, attachmentDataUrl);
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);
            
            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const updateResponse: AxiosResponse<UpdateOrderResponse> = yield call(
                {
                    context: client,
                    fn: client.updateOrder,
                },
                currentOrder.orderId,
                {
                    ...currentOrder,
                    patientDemographic: base64Attachment,
                }
            );

            if(updateResponse.status === 401)
            {
                yield put(unauthorized(undefined));
                return;     
            }
            
            if(updateResponse.status !== 204)
                throw new Error("Error communicating with Api");

            yield put(ordersSearchSlice.actions.setAttachmentUploadFile(undefined));
            yield call({ context: OrderSagas, fn: OrderSagas.apiLoadOrderAttachments}, currentOrder?.orderId);
            toast.success("Attachment uploaded.");
        }
        catch(e: any){
            toast.error('Error uploading patient attachment. Please try again.');
            console.log(`[OrderSagas:saveOrderAttachment] the following exception has occurred`, e);
        }
        finally {
            yield put(ordersSearchSlice.actions.setUploadAttachmentState('READY'));
        }
    }
    private static* viewOrder(action: ReturnType<typeof viewOrder>){
        const { payload: { order, rowIndex, tabIndex }} = action;
        try {
            yield put(ordersSearchSlice.actions.setSelectedOrder(order));
            yield call({ context: OrderSagas, fn: OrderSagas.apiLoadOrderAttachments}, order?.orderId);
            yield call({ context: OrderSagas, fn: OrderSagas.apiLoadOrderReports}, order?.orderId);
            yield put(ordersSearchSlice.actions.setSelectedOrderRowIndex(rowIndex));
            yield put(ordersSearchSlice.actions.setOrderDetailsTab(tabIndex ?? order));
        }
        catch(e: any){
            toast.error('Error Viewing Order');
            console.log(`[OrderSagas:viewOrder] the following exception has occurred`, e);
        }
    }

    private static* apiLoadOrderAttachments(orderId: number | string | undefined) {
        try {
            if(orderId === undefined){
                yield put(ordersSearchSlice.actions.setSelectedOrderAttachments(undefined));
                return;
            }

            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);
            
            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const orderAttachmentsResponse: AxiosResponse<OrderItemAttachment> = yield call(
                {
                    context: client,
                    fn: client.getOrderAttachments,
                },
                orderId
            );

            if(orderAttachmentsResponse.status === 401)
            {
                yield put(unauthorized(undefined));
                return;     
            }
            
            if(orderAttachmentsResponse.status !== 200)
                throw new Error("Error communicating with Api");

            yield put(ordersSearchSlice.actions.setSelectedOrderAttachments(orderAttachmentsResponse.data));
        }
        catch(e: any){
            toast.error('Error retrieving order attachments.');
            console.log(`[OrderSagas:apiLoadOrderAttachments] the following exception has occurred`, e);
        }
    }

    private static* apiLoadOrderReports(orderId: number | string | undefined) {
        try {
            if(orderId === undefined){
                yield put(ordersSearchSlice.actions.setSelectedOrderReports(undefined));
                return;
            }

            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);
            
            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const orderReportsResponse: AxiosResponse<OrderItemReports> = yield call(
                {
                    context: client,
                    fn: client.getOrderReports,
                },
                orderId
            );

            if(orderReportsResponse.status === 401)
            {
                yield put(ordersSearchSlice.actions.setSelectedOrderReports(undefined));
                yield put(unauthorized(undefined));
                return;
            }

            if(orderReportsResponse.status !== 200)
                throw new Error("Error communicating with Api");

            yield put(ordersSearchSlice.actions.setSelectedOrderReports(orderReportsResponse.data));
        }
        catch(e: any){
            // Ensure a fresh (empty) reference lands in state even on failure — the
            // Reports tab's loading skeleton only clears when this reference changes,
            // so leaving it untouched here left the skeleton stuck forever on error.
            yield put(ordersSearchSlice.actions.setSelectedOrderReports(undefined));
            toast.error('Error retrieving order reports.');
            console.log(`[OrderSagas:apiLoadOrderReports] the following exception has occurred`, e);
        }
    }

    private static* loadOrder(action: ReturnType<typeof loadOrder>){
        const { payload: { id } } = action;
        try {
            yield put(ordersSearchSlice.actions.setOrderState('BUSY'));
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);
            
            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const orderResponse: AxiosResponse<OrderItem> = yield call(
                {
                    context: client,
                    fn: client.getOrder,
                },
                id
            );

            if(orderResponse.status === 401)
            {
                yield put(unauthorized(undefined));
                return;     
            }
            
            if(orderResponse.status !== 200)
                throw new Error("Error communicating with Api");

            yield put(ordersSearchSlice.actions.setSelectedOrder(orderResponse.data));
        }
        catch(e: any){
            toast.error('Error Loading Order');
            console.log(`[OrderSagas:loadOrder] the following exception has occurred`, e);
        }
        finally {
            yield put(ordersSearchSlice.actions.setOrderState('READY'));
        }
    }

    private static* debounceFilterOrders(){
        try {

            /*
                over-engineered but, debounce the api calls for searching
                debounces for 1s from the last search and cancels the debounce if a loadOrders action
                gets sent through the middleware.
            */
            while(true){
                yield take(filterOrders);
                const [_, runFilter] = yield race([
                    take(loadOrders),
                    delay(50),
                ]);
	        console.log("filter order loop iteration...");
                if(runFilter){
                    yield put(loadOrders({}));
                }
            }
        }
        catch(e: any){
            console.log(e);
        }
    }

    private static* debounceFilterKitOrders(){
        try {
            while(true){
                yield take(filterKitOrders);
                const [_, runFilter] = yield race([
                    take(loadKitOrders),
                    delay(50),
                ]);
                if(runFilter){
                    yield put(loadKitOrders({}));
                }
            }
        }
        catch(e: any){
            console.log(e);
        }
    }

    // Pre-Buy Phase 6 - Kit Orders grid now reads nuvia_orders via NuviaOrdersController, not the
    // shared /orders/search. Mapped onto OrderItem's shape so KitOrdersRow/OrderFilterPopover/
    // TrackingTimelineModal (all built against OrderItem before the split) didn't need a parallel
    // rewrite - nuviaOrderId becomes orderId, which is exactly what checkOrderTracking expects too.
    private static mapNuviaOrderToOrderItem(order: NuviaOrderItem): OrderItem {
        return {
            orderId: order.nuviaOrderId,
            active: order.active,
            orderStatus: order.orderStatus ?? undefined,
            orderDatetime: order.orderDatetime,
            // Sep 2026 - was missing from this mapper, same class of gap already fixed for
            // returnTrackingNumber below - so row.organizationId (needed by CheckOrderTracking's
            // Portal Admin cross-org fix, see KitOrdersRow's CheckTrackingButton) was always
            // undefined here regardless of what NuviaOrdersController.Search actually returns,
            // silently falling back to the caller's own home org every time.
            organizationId: order.organizationId,
            organizationName: order.organizationName,
            organizationState: order.organizationState,
            confirmationNumber: order.confirmationNumber,
            quantity: order.quantity,
            preBuyOrderType: order.preBuyOrderType,
            kitsCreatedCount: order.kitsCreatedCount,
            shippingFailed: order.shippingFailed,
            patientPaymentPending: order.patientPaymentPending,
            hasShipmentTracking: order.hasShipmentTracking,
            internalComment: order.internalComment,
            shippingRequestId: order.shippingRequestId,
            shippedTrackingNumber: order.shippedTrackingNumber,
            // Aug 2026 - was missing from this mapper, so the grid row (and everything reading
            // row.returnTrackingNumber, like SupplyOrderDetailsDrawer's Return leg field/button)
            // never saw it even though NuviaOrdersController.Search already returns it.
            returnTrackingNumber: order.returnTrackingNumber,
        };
    }

    private static* loadKitOrders(action: ReturnType<typeof loadKitOrders>){
        try {
            yield put(ordersSearchSlice.actions.setState('BUSY'));
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);

            const orderGridParams: OrderGridParams = yield select(selectOrdersSearchGridParams);

            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const ordersResponse: AxiosResponse<NuviaOrdersResponse> = yield call(
                {
                    context: client,
                    fn: client.searchNuviaOrders
                },
                {
                    ...orderGridParams,
                }
            );

            if(ordersResponse.status === 401)
            {
                yield put(unauthorized(undefined));
                return;
            }

            if(ordersResponse.status !== 200)
                throw new Error("Error communicating with Api");

            const { orders } = ordersResponse.data;

            yield put(ordersSearchSlice.actions.setOrders(orders.map(OrderSagas.mapNuviaOrderToOrderItem)));
            yield put(ordersSearchSlice.actions.setState('READY'));
        }
        catch(e: any){
            toast.error('Error Loading Kit Orders');
            console.log(`[OrderSagas:loadKitOrders] the following exception has occurred`, e);
        }
    }

    private static* loadOrders(action: ReturnType<typeof loadOrders>){
        try {
            yield put(ordersSearchSlice.actions.setState('BUSY'));
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);

            const orderGridParams: OrderGridParams = yield select(selectOrdersSearchGridParams);
            
            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const ordersResponse: AxiosResponse<OrdersResponse> = yield call(
                {
                    context: client,
                    fn: client.searchOrders
                },
                {
                    ...orderGridParams,
                }
            );

            if(ordersResponse.status === 401)
            {
                yield put(unauthorized(undefined));
                return;     
            }
            
            if(ordersResponse.status !== 200)
                throw new Error("Error communicating with Api");

            const { orders, previousPage, nextPage } = ordersResponse.data;

            yield put(ordersSearchSlice.actions.setOrders(orders));
            yield put(ordersSearchSlice.actions.setState('READY'));
        }
        catch(e: any){
            toast.error('Error Loading Orders');
            console.log(`[OrderSagas:loadOrders] the following exception has occurred`, e);
        }
    }
}
